import axios, { type AxiosInstance } from 'axios'

import { createBroadcastChannel } from './createBroadcastChannel'

// 이 서비스가 직접 부르는 토큰 발급 엔드포인트(/v2/reissue·/v2/x-auth-token-to-jwt) 응답 중 읽는 필드만 둔다.
// 이 파일은 domain 보다 아래에 있어 생성 타입을 import 할 수 없다.
type TokenIssueResponse = {
  accessToken: string
  refreshToken: string
  restrictions?: string[]
}

type TokenData = {
  accessToken: string | null
  refreshToken: string | null
  legacyToken: string | null
}

type EventHandlers = {
  onTokenCleared?: () => void
  onTokenNotFound?: () => void
  onTokenReceived?: () => void
  onReissueSuccess?: (data: Pick<TokenIssueResponse, 'restrictions'>) => void
}

type Environment = 'dev' | 'stg' | 'live' | 'custom'

const getBaseURL = (env: Environment, customURL?: string): string => {
  switch (env) {
    case 'live':
      return 'https://api.mathflat.com'
    case 'stg':
      return 'https://api-staging.mathflat.com'
    case 'dev':
      return 'https://api-dev.mathflat.com'
    case 'custom':
      if (customURL === 'dev') return 'https://api-dev.mathflat.com'
      if (!customURL) {
        throw new Error('Custom URL is required when environment is "custom"')
      }
      return customURL
  }
}
export const TOKEN_KEYS = {
  accessToken: 'accessToken',
  refreshToken: 'refreshToken',
  legacyToken: 'x-auth-token',
} as const

/**
 * JWT 토큰 관리 및 인증 서비스
 *
 * - 토큰 저장/조회/재발급
 * - 로그인/로그아웃
 * - 레거시 토큰 마이그레이션
 *
 * Teacher, Student 앱 모두 동일한 인스턴스를 사용합니다.
 */

class JwtTokenService {
  private _tokenData: TokenData = {
    accessToken: null,
    refreshToken: null,
    legacyToken: null,
  }

  private broadcastChannel = createBroadcastChannel('jwt-token')

  private authAxios: AxiosInstance | null = null
  private eventHandlers: EventHandlers = {}

  currentStorage: typeof localStorage | typeof sessionStorage = sessionStorage

  get tokenStatus(): 'VALID' | 'INVALID' | 'NEED_MIGRATION' {
    const { legacyToken, accessToken } = this._tokenData

    if (legacyToken && !accessToken) return 'NEED_MIGRATION'
    if (!accessToken) return 'INVALID'
    return 'VALID'
  }

  get isLoggedIn(): boolean {
    const { accessToken, refreshToken } = this._tokenData
    return accessToken !== null && refreshToken !== null
  }

  get isUsingLocalStorage(): boolean {
    return this.currentStorage === localStorage
  }

  constructor() {
    // 토큰 로드 (localStorage 우선, 없으면 sessionStorage)
    this._tokenData = {
      accessToken:
        localStorage.getItem(TOKEN_KEYS.accessToken) ||
        sessionStorage.getItem(TOKEN_KEYS.accessToken),
      refreshToken:
        localStorage.getItem(TOKEN_KEYS.refreshToken) ||
        sessionStorage.getItem(TOKEN_KEYS.refreshToken),
      legacyToken:
        localStorage.getItem(TOKEN_KEYS.legacyToken) ||
        sessionStorage.getItem(TOKEN_KEYS.legacyToken),
    }

    // localStorage에 토큰이 있으면 localStorage 사용
    this.currentStorage = localStorage.getItem(TOKEN_KEYS.accessToken)
      ? localStorage
      : sessionStorage

    this._setupBroadcast()
  }

  /**
   * 인증 axios 인스턴스 초기화 (앱 진입 시 1회 호출)
   *
   * @example
   * jwtTokenService.init({ env: 'live', platform: 'STUDENT' })
   * jwtTokenService.init({ env: 'custom', customURL: 'https://...', platform: 'TEACHER_WEB' })
   */
  init(config: { env: Environment; platform: string; customURL?: string }): void {
    this.authAxios = axios.create({
      baseURL: getBaseURL(config.env, config.customURL),
      headers: {
        'Content-Type': 'application/json',
        'x-platform': config.platform,
      },
    })
  }

  /**
   * 이미 초기화된 인스턴스의 baseURL만 교체 (DevToolbar 환경 전환용)
   */
  setEnvironment(env: Environment, customURL?: string): void {
    this.ensureAuthAxios().defaults.baseURL = getBaseURL(env, customURL)
  }

  /**
   * 이벤트 핸들러 설정 (앱 레이어에서 redirect/reload 동작 구현)
   *
   * @param handlers - 이벤트 핸들러 객체
   *
   * @example
   * jwtTokenService.setEventHandlers({
   *   onTokenCleared: () => window.location.replace('/login'),
   *   onTokenNotFound: () => window.location.replace('/login'),
   *   onTokenReceived: () => window.location.reload(),
   * })
   */
  setEventHandlers(handlers: EventHandlers): void {
    this.eventHandlers = { ...this.eventHandlers, ...handlers }
  }

  private ensureAuthAxios(): AxiosInstance {
    if (!this.authAxios) {
      throw new Error(
        'jwtTokenService is not initialized. Please call init() before making API requests.',
      )
    }
    return this.authAxios
  }

  /**
   * localStorage 사용 모드로 전환 (로그인 상태 유지)
   * 이미 sessionStorage에 토큰이 있다면 localStorage로 이동시킵니다
   */
  useLocalStorage(): void {
    // 1. localStorage 사용으로 전환
    this.currentStorage = localStorage

    // 2. 이미 sessionStorage에 토큰이 있다면 localStorage로 이동
    const sessionAccessToken = sessionStorage.getItem(TOKEN_KEYS.accessToken)
    const sessionRefreshToken = sessionStorage.getItem(TOKEN_KEYS.refreshToken)
    const sessionLegacyToken = sessionStorage.getItem(TOKEN_KEYS.legacyToken)

    if (sessionAccessToken) {
      localStorage.setItem(TOKEN_KEYS.accessToken, sessionAccessToken)
      sessionStorage.removeItem(TOKEN_KEYS.accessToken)
    }
    if (sessionRefreshToken) {
      localStorage.setItem(TOKEN_KEYS.refreshToken, sessionRefreshToken)
      sessionStorage.removeItem(TOKEN_KEYS.refreshToken)
    }
    if (sessionLegacyToken) {
      localStorage.setItem(TOKEN_KEYS.legacyToken, sessionLegacyToken)
      sessionStorage.removeItem(TOKEN_KEYS.legacyToken)
    }
  }

  /**
   * sessionStorage 사용 모드로 전환
   * 이미 localStorage에 토큰이 있다면 sessionStorage로 이동
   * 통합 마이그레이션 hash 흐름 (`migrateFromHashParam`)에서 isPersist=false 일 때 명시적으로 호출.
   * ex) hash = student-up 세션스토리지 사용자가 새로고침 후 student로 왔을때 -> 아마 엣지케이스일것. 실제로 student는 ls이면서 student-up이 ss일 경우 희박..
   * `useLocalStorage()`는 isKeepSignInChecked=true 시 자동 호출되지만, false 케이스는
   * 호출이 없어 이전 진입의 mode 가 그대로 유지되는 문제가 있어 명시 호출 메서드 추가.
   */
  useSessionStorage(): void {
    this.currentStorage = sessionStorage

    const localAccessToken = localStorage.getItem(TOKEN_KEYS.accessToken)
    const localRefreshToken = localStorage.getItem(TOKEN_KEYS.refreshToken)
    const localLegacyToken = localStorage.getItem(TOKEN_KEYS.legacyToken)

    if (localAccessToken) {
      sessionStorage.setItem(TOKEN_KEYS.accessToken, localAccessToken)
      localStorage.removeItem(TOKEN_KEYS.accessToken)
    }
    if (localRefreshToken) {
      sessionStorage.setItem(TOKEN_KEYS.refreshToken, localRefreshToken)
      localStorage.removeItem(TOKEN_KEYS.refreshToken)
    }
    if (localLegacyToken) {
      sessionStorage.setItem(TOKEN_KEYS.legacyToken, localLegacyToken)
      localStorage.removeItem(TOKEN_KEYS.legacyToken)
    }
  }

  getAccessToken(): string | null {
    return this._tokenData.accessToken
  }

  getRefreshToken(): string | null {
    return this._tokenData.refreshToken
  }

  getLegacyToken(): string | null {
    return this._tokenData.legacyToken
  }

  /**
   * 새로운 토큰을 저장하고 다른 탭에 브로드캐스트합니다
   *
   * @param updates - 업데이트할 토큰 데이터
   * @param broadcastType - 브로드캐스트 타입 ('LOGIN' | 'TOKEN_UPDATED' | null)
   *   - 'LOGIN': 로그인 시 (다른 탭들도 reload)
   *   - 'TOKEN_UPDATED': 토큰 재발급 시
   *   - null: 브로드캐스트 하지 않음
   */
  setTokens(
    updates: Partial<TokenData>,
    broadcastType: 'LOGIN' | 'TOKEN_UPDATED' | null = 'TOKEN_UPDATED',
    extras?: Pick<TokenIssueResponse, 'restrictions'>,
  ): void {
    this._tokenData = { ...this._tokenData, ...updates }

    // storage에 저장 또는 삭제
    // null/undefined는 명시적 삭제 의도, truthy만 저장
    if ('accessToken' in updates) {
      if (updates.accessToken) {
        this.currentStorage.setItem(TOKEN_KEYS.accessToken, updates.accessToken)
      } else {
        this.currentStorage.removeItem(TOKEN_KEYS.accessToken)
      }
    }
    if ('refreshToken' in updates) {
      if (updates.refreshToken) {
        this.currentStorage.setItem(TOKEN_KEYS.refreshToken, updates.refreshToken)
      } else {
        this.currentStorage.removeItem(TOKEN_KEYS.refreshToken)
      }
    }
    if ('legacyToken' in updates) {
      if (updates.legacyToken) {
        this.currentStorage.setItem(TOKEN_KEYS.legacyToken, updates.legacyToken)
      } else {
        this.currentStorage.removeItem(TOKEN_KEYS.legacyToken)
      }
    }

    if (broadcastType) {
      this.broadcastChannel?.postMessage({
        message: broadcastType,
        payload: { ...this._tokenData, ...extras },
      })
    }
  }

  /**
   * 토큰을 삭제하고 다른 탭에 브로드캐스트합니다
   * localStorage와 sessionStorage 양쪽 모두에서 제거합니다
   */
  clearTokens(shouldBroadcast = true): void {
    this._tokenData = { accessToken: null, refreshToken: null, legacyToken: null }
    this.currentStorage = sessionStorage

    // 양쪽 storage 모두 제거
    localStorage.removeItem(TOKEN_KEYS.accessToken)
    localStorage.removeItem(TOKEN_KEYS.refreshToken)
    localStorage.removeItem(TOKEN_KEYS.legacyToken)
    sessionStorage.removeItem(TOKEN_KEYS.accessToken)
    sessionStorage.removeItem(TOKEN_KEYS.refreshToken)
    sessionStorage.removeItem(TOKEN_KEYS.legacyToken)

    if (shouldBroadcast) {
      this.broadcastChannel?.postMessage({ message: 'TOKEN_CLEARED' })
    }
  }

  requestToken() {
    this.broadcastChannel?.postMessage({ message: 'REQUEST_TOKEN' })
  }

  /**
   * 토큰 재발급
   */
  reissueTokens = async () => {
    const {
      data: { accessToken, refreshToken, restrictions },
    } = await this.ensureAuthAxios().post<TokenIssueResponse>('/v2/reissue', {
      refreshToken: this._tokenData.refreshToken,
    })

    this.setTokens({ accessToken, refreshToken }, 'TOKEN_UPDATED', { restrictions })
    this.eventHandlers.onReissueSuccess?.({ restrictions })

    return { accessToken, refreshToken, restrictions }
  }

  /**
   * JWT 로그인 응답 처리
   */
  processJwtLoginResponse = async ({
    accessToken,
    refreshToken,
  }: {
    accessToken: string
    refreshToken: string
  }) => {
    const legacyToken = await this.syncLegacyTokenFromJwt(accessToken)

    // 로그인 시에는 'LOGIN' 메시지로 브로드캐스트
    this.setTokens(
      {
        accessToken,
        refreshToken,
        legacyToken,
      },
      'LOGIN',
    )
  }

  /**
   * 레거시 토큰만 있는 유저의 경우 마이그레이션을 통하여
   * JWT 토큰으로 재발급을 합니다.
   *
   * @param legacyToken
   */
  migrateFromLegacyToken = async (legacyToken: string) => {
    const {
      data: { accessToken, refreshToken },
    } = await this.ensureAuthAxios().post<TokenIssueResponse>(
      '/v2/x-auth-token-to-jwt',
      {},
      { headers: { 'x-auth-token': legacyToken } },
    )

    await this.processJwtLoginResponse({ accessToken, refreshToken })
  }

  /**
   * 로그인 이후 JWT 토큰으로 레거시 토큰을 최신화 합니다.
   *
   * 기존 레거시 토큰은 생명 주기가 짧기 때문에
   * 더 이상 reissue를 하지 않는 레거시 토큰은
   * 생명 주기가 긴 토큰으로 재발급을 해야합니다.
   *
   * @param accessToken JWT 토큰
   * @throws 레거시 토큰 동기화 실패 시 에러를 throw하고 모든 토큰을 클리어
   */
  syncLegacyTokenFromJwt = async (accessToken: string) => {
    try {
      const {
        data: { token: legacyToken },
      } = await this.ensureAuthAxios().post<{ token: string }>(
        '/v2/jwt-to-x-auth-token',
        {},
        { headers: { Authorization: `Bearer ${accessToken}` } },
      )

      this.setTokens({ legacyToken })

      return legacyToken
    } catch (error) {
      // 레거시 토큰 동기화 실패는 JWT 토큰도 문제가 있을 가능성이 높음
      this.clearTokens()
      throw error
    }
  }

  /**
   * 앱 초기화 시 인증 상태 체크 및 마이그레이션
   *
   * 실행 순서:
   * 1. 레거시 토큰 있으면 마이그레이션 시도
   * 2. JWT 토큰 존재 여부 확인
   *
   * Note: 토큰 만료 여부는 서버에서 검증합니다 (401 응답 시 자동 재발급)
   *
   * @returns 인증된 상태인지 여부
   * @throws 마이그레이션 실패 시 에러를 throw (호출하는 쪽에서 처리 필요)
   */
  initializeAuth = async (): Promise<boolean> => {
    const { accessToken, legacyToken } = this._tokenData

    // 아무 토큰도 없으면 로그인 상태가 아님
    if (!accessToken && !legacyToken) {
      this.requestToken()
    }

    // JWT 토큰만 있으면 레거시 토큰 조회
    if (accessToken && !legacyToken) {
      await this.syncLegacyTokenFromJwt(accessToken)
    }

    // 레거시 토큰만 있으면 마이그레이션 시도
    if (legacyToken && !accessToken) {
      try {
        await this.migrateFromLegacyToken(legacyToken)
      } catch (error) {
        this.clearTokens()
        throw error
      }
    }

    return this.isLoggedIn
  }

  private _setupBroadcast(): void {
    if (!this.broadcastChannel) return

    this.broadcastChannel.onmessage = (event: MessageEvent) => {
      const data = event.data

      switch (data.message) {
        case 'LOGIN':
          // 다른 탭에서 로그인함
          this.setTokens(data.payload, null)
          this.eventHandlers.onTokenReceived?.()
          break

        case 'TOKEN_UPDATED': {
          // 다른 탭에서 토큰 재발급
          const { restrictions, ...tokenUpdates } = data.payload
          this.setTokens(tokenUpdates, null)
          this.eventHandlers.onReissueSuccess?.({ restrictions })
          break
        }

        case 'REQUEST_TOKEN':
          // 다른 탭이 토큰 요청
          // 토큰 있으면 TOKEN으로 전송, 없으면 TOKEN_NOT_FOUND로 전송
          if (this._tokenData.accessToken) {
            this.broadcastChannel?.postMessage({
              message: 'TOKEN',
              payload: { token: this._tokenData },
            })
          } else {
            this.broadcastChannel?.postMessage({ message: 'TOKEN_NOT_FOUND' })
          }
          break

        case 'TOKEN':
          // 내가 요청한 토큰 응답 받음
          this.setTokens({ ...data.payload.token }, null)
          this.eventHandlers.onTokenReceived?.()
          break

        case 'TOKEN_NOT_FOUND':
          // 다른 탭도 토큰 없음
          this.eventHandlers.onTokenNotFound?.()
          break

        case 'TOKEN_CLEARED':
          // 다른 탭에서 로그아웃함
          this.clearTokens(false)
          this.eventHandlers.onTokenCleared?.()
          break
      }
    }
  }
}

export { JwtTokenService }
export const jwtTokenService = new JwtTokenService()
